import { Box, FormHelperText, InputLabel, useTheme } from '@mui/material';
import { highlight, languages } from 'prismjs';
import type { FC } from 'react';
import 'prismjs/components/prism-json';
import { useEffect, useState } from 'react';
import Editor from 'react-simple-code-editor';

import { hasStringValue, hasValue } from '@core/utils/equality';

export type JsonConfigEditorProps = {
  value: string;
  onChange: (value: string) => void;
  error?: boolean;
  helperText?: string;
  label?: string;
  disabled?: boolean;
};

export const JsonConfigEditor: FC<JsonConfigEditorProps> = ({
  value,
  onChange,
  error: externalError,
  helperText: externalHelperText,
  label = 'Configuration (JSON)',
  disabled = false,
}) => {
  const [internalError, setInternalError] = useState<string | null>(null);
  const theme = useTheme();

  const handleBlur = (): void => {
    if (!hasStringValue(value)) {
      setInternalError(null);
      return;
    }

    try {
      const parsed = JSON.parse(value);
      // Prettify the JSON on blur
      const prettified = JSON.stringify(parsed, null, 2);
      onChange(prettified);
      setInternalError(null);
    } catch (e) {
      // Extract error details from the JSON parse error
      const errorMessage = e instanceof Error ? e.message : 'Invalid JSON format';

      // Try to extract line/column info from error message
      // Error messages typically look like: "Unexpected token } in JSON at position 42"
      const positionMatch = errorMessage.match(/position (\d+)/);
      if (hasValue(positionMatch)) {
        const position = parseInt(positionMatch[1], 10);
        const lines = value.substring(0, position).split('\n');
        const lineNum = lines.length;
        const colNum = lines[lines.length - 1].length + 1;
        setInternalError(`Invalid JSON at line ${lineNum}, column ${colNum}: ${errorMessage}`);
      } else {
        setInternalError(`Invalid JSON: ${errorMessage}`);
      }
    }
  };

  const handleValueChange = (newValue: string): void => {
    onChange(newValue);
    // Clear error while typing
    if (hasStringValue(internalError)) {
      setInternalError(null);
    }
  };

  const showError = externalError === true || hasStringValue(internalError);

  let errorMessage: string | undefined;
  if (hasStringValue(externalHelperText)) {
    errorMessage = externalHelperText;
  } else if (hasStringValue(internalError)) {
    errorMessage = internalError ?? undefined;
  } else {
    errorMessage = undefined;
  }

  // Add Prism CSS styles dynamically
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .token.property { color: #9cdcfe; }
      .token.string { color: #ce9178; }
      .token.number { color: #b5cea8; }
      .token.boolean { color: #569cd6; }
      .token.null { color: #569cd6; }
      .token.punctuation { color: #d4d4d4; }
      .token.operator { color: #d4d4d4; }
    `;
    document.head.appendChild(style);
    return (): void => {
      document.head.removeChild(style);
    };
  }, []);

  return (
    <Box>
      <InputLabel error={showError}>{label}</InputLabel>
      <Box
        sx={{
          mt: 1,
          border: `1px solid ${showError ? theme.palette.error.main : theme.palette.divider}`,
          borderRadius: 1,
          '&:focus-within': {
            borderColor: showError ? theme.palette.error.main : theme.palette.primary.main,
            borderWidth: 2,
          },
        }}
      >
        <Editor
          value={value}
          onValueChange={handleValueChange}
          onBlur={handleBlur}
          highlight={(code) => highlight(code, languages.json, 'json')}
          padding={14}
          disabled={disabled}
          placeholder='Enter JSON configuration'
          style={{
            fontFamily: 'Consolas, Monaco, "Courier New", monospace',
            fontSize: '14px',
            lineHeight: '21px',
            minHeight: '400px',
            backgroundColor: '#1e1e1e',
            color: '#d4d4d4',
            borderRadius: '4px',
          }}
          textareaClassName='json-editor-textarea'
        />
      </Box>
      <FormHelperText error={showError}>
        {hasStringValue(errorMessage)
          ? errorMessage
          : 'Enter valid JSON configuration for this integration'}
      </FormHelperText>
    </Box>
  );
};
